// SupportCrewPanel.jsx - Manage ground support staff

import React, { useState } from 'react';
import { SUPPORT_ROLES } from '../../systems/SupportCrewSystem';

export function SupportCrewPanel({ supportState, cash, onHire, onFire, onClose }) {
  const [viewMode, setViewMode] = useState('roster'); // roster | market
  const [filterRole, setFilterRole] = useState(null);
  
  const roster = supportState?.roster || { mechanics: [], fleet_operators: [], engineers: [] };
  const market = supportState?.market || [];
  
  const allRoster = [
    ...roster.mechanics,
    ...roster.fleet_operators,
    ...roster.engineers,
  ];

  const filteredMarket = filterRole 
    ? market.filter(c => c.role === filterRole)
    : market;

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        
        <div style={styles.header}>
          <div style={styles.headerTitle}>SUPPORT CREW</div>
          <div style={styles.tabs}>
            <button 
              style={{...styles.tab, ...(viewMode === 'roster' ? styles.tabActive : {})}}
              onClick={() => setViewMode('roster')}
            >
              ROSTER ({allRoster.length})
            </button>
            <button 
              style={{...styles.tab, ...(viewMode === 'market' ? styles.tabActive : {})}}
              onClick={() => setViewMode('market')}
            >
              MARKET ({market.length})
            </button>
          </div>
          <button style={styles.closeButton} onClick={onClose}>X</button>
        </div>

        {/* Roster View */}
        {viewMode === 'roster' && (
          <div style={styles.content}>
            {allRoster.length === 0 ? (
              <div style={styles.emptyState}>
                No support staff hired. Your airline is operating without dedicated ground crew.
                <br/><br/>
                Without mechanics, expect higher maintenance costs and more AOG events.
              </div>
            ) : (
              <>
                <div style={styles.summary}>
                  Monthly payroll: ${formatMoney(supportState?.totalMonthlySalary || 0)}
                </div>
                
                {/* Mechanics */}
                {roster.mechanics.length > 0 && (
                  <RosterSection 
                    title="MECHANICS" 
                    crew={roster.mechanics}
                    onFire={onFire}
                  />
                )}
                
                {/* Fleet Operators */}
                {roster.fleet_operators.length > 0 && (
                  <RosterSection 
                    title="FLEET OPERATORS" 
                    crew={roster.fleet_operators}
                    onFire={onFire}
                  />
                )}
                
                {/* Engineers */}
                {roster.engineers.length > 0 && (
                  <RosterSection 
                    title="ENGINEERS" 
                    crew={roster.engineers}
                    onFire={onFire}
                  />
                )}
              </>
            )}
          </div>
        )}

        {/* Market View */}
        {viewMode === 'market' && (
          <div style={styles.content}>
            <div style={styles.filterBar}>
              <span style={styles.filterLabel}>Filter:</span>
              <button
                style={{...styles.filterBtn, ...(!filterRole ? styles.filterActive : {})}}
                onClick={() => setFilterRole(null)}
              >
                ALL
              </button>
              {Object.values(SUPPORT_ROLES).map(role => (
                <button
                  key={role.role}
                  style={{...styles.filterBtn, ...(filterRole === role.role ? styles.filterActive : {})}}
                  onClick={() => setFilterRole(role.role)}
                >
                  {role.title.toUpperCase()}S
                </button>
              ))}
            </div>
            
            <div style={styles.grid}>
              {filteredMarket.map(crew => {
                const totalCost = crew.salary + crew.signingBonus;
                const canAfford = cash >= totalCost;
                return (
                  <SupportCrewCard
                    key={crew.id}
                    crew={crew}
                    canAfford={canAfford}
                    onAction={() => canAfford && onHire(crew.id)}
                  />
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

function RosterSection({ title, crew, onFire }) {
  return (
    <div style={styles.rosterSection}>
      <div style={styles.sectionTitle}>{title}</div>
      <div style={styles.grid}>
        {crew.map(c => (
          <SupportCrewCard 
            key={c.id} 
            crew={c} 
            isHired 
            onAction={() => onFire(c.id)}
          />
        ))}
      </div>
    </div>
  );
}

function SupportCrewCard({ crew, isHired, canAfford = true, onAction }) {
  const tierColors = {
    apprentice: '#888',
    journeyman: '#4488ff',
    veteran: '#aa44ff',
    expert: '#ffaa00',
  };

  const roleConfig = SUPPORT_ROLES[crew.role.toUpperCase()];
  const statNames = roleConfig ? Object.keys(roleConfig.baseStats) : [];

  return (
    <div style={{...styles.card, ...(canAfford ? {} : styles.cardLocked)}}>
      {/* Header */}
      <div style={styles.cardHeader}>
        <div>
          <div style={styles.crewName}>{crew.name}</div>
          <div style={styles.crewTitle}>{crew.title}</div>
        </div>
        <div style={{...styles.tierBadge, borderColor: tierColors[crew.tier], color: tierColors[crew.tier]}}>
          {crew.tier?.toUpperCase()}
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsGrid}>
        {statNames.map(stat => (
          <StatBar key={stat} label={stat.substring(0, 3).toUpperCase()} value={crew.stats[stat]} />
        ))}
      </div>

      {/* Experience */}
      <div style={styles.experience}>
        {crew.yearsExperience} years experience
      </div>

      {/* Certifications */}
      <div style={styles.certsSection}>
        <div style={styles.certsLabel}>CERTIFICATIONS</div>
        <div style={styles.certs}>
          {crew.certifications?.map(cert => (
            <span key={cert} style={styles.certBadge}>{cert}</span>
          ))}
        </div>
      </div>

      {/* Traits */}
      {crew.traits?.length > 0 && (
        <div style={styles.traitsSection}>
          {crew.traits.map(trait => (
            <span 
              key={trait} 
              style={{
                ...styles.traitBadge,
                ...(isPositiveTrait(trait) ? styles.traitPositive : styles.traitNegative)
              }}
            >
              {formatTrait(trait)}
            </span>
          ))}
        </div>
      )}

      {/* Footer */}
      <div style={styles.cardFooter}>
        {isHired ? (
          <>
            <div style={styles.salary}>${formatMoney(crew.salary)}/mo</div>
            <button style={styles.fireBtn} onClick={onAction}>Release</button>
          </>
        ) : (
          <>
            <div>
              <div style={styles.salary}>${formatMoney(crew.salary)}/mo</div>
              <div style={styles.bonus}>+ ${formatMoney(crew.signingBonus)} signing</div>
            </div>
            <button 
              style={{...styles.hireBtn, ...(canAfford ? {} : styles.btnLocked)}}
              onClick={onAction}
              disabled={!canAfford}
            >
              {canAfford ? 'Hire' : 'No Funds'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function StatBar({ label, value }) {
  const getColor = (v) => {
    if (v >= 80) return '#00ff88';
    if (v >= 60) return '#88ff00';
    if (v >= 40) return '#ffcc00';
    return '#ff6600';
  };

  return (
    <div style={styles.statBar}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statTrack}>
        <div 
          style={{
            ...styles.statFill,
            width: `${value}%`,
            backgroundColor: getColor(value),
          }}
        />
      </div>
      <div style={styles.statValue}>{value}</div>
    </div>
  );
}

function formatMoney(cents) {
  if (!cents) return '0';
  if (cents >= 100000000) return `${(cents / 100000000).toFixed(1)}M`;
  if (cents >= 100000) return `${(cents / 100000).toFixed(0)}K`;
  return (cents / 100).toLocaleString();
}

function formatTrait(trait) {
  return trait.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function isPositiveTrait(trait) {
  const positive = ['detail_oriented', 'team_leader', 'fast_learner', 'night_owl', 
                    'by_the_book', 'creative_solutions', 'mentor', 'calm_under_pressure'];
  return positive.includes(trait);
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  panel: {
    width: '950px', maxHeight: '90vh', overflow: 'hidden',
    backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '8px',
    fontFamily: 'inherit',
    display: 'flex', flexDirection: 'column',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: '1px solid #222',
  },
  headerTitle: {
    fontSize: '13px', fontWeight: '600', letterSpacing: '2px', color: '#fff',
  },
  tabs: { display: 'flex', gap: '4px' },
  tab: {
    background: 'none', border: '1px solid #333', borderRadius: '3px',
    padding: '6px 12px', fontSize: '10px', color: '#666', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  tabActive: { borderColor: '#00ff88', color: '#00ff88' },
  closeButton: {
    background: 'none', border: 'none', color: '#666', fontSize: '24px', cursor: 'pointer',
  },
  content: { flex: 1, overflow: 'auto', padding: '20px' },
  emptyState: { 
    color: '#666', fontSize: '12px', textAlign: 'center', padding: '40px',
    lineHeight: '1.6',
  },
  summary: {
    fontSize: '11px', color: '#888', marginBottom: '20px', padding: '10px',
    backgroundColor: '#111', borderRadius: '4px',
  },
  rosterSection: { marginBottom: '24px' },
  sectionTitle: {
    fontSize: '10px', letterSpacing: '2px', color: '#666', marginBottom: '12px',
  },
  filterBar: {
    display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px',
    flexWrap: 'wrap',
  },
  filterLabel: { fontSize: '10px', color: '#666' },
  filterBtn: {
    background: 'none', border: '1px solid #333', borderRadius: '3px',
    padding: '4px 10px', fontSize: '9px', color: '#666', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  filterActive: { borderColor: '#00ff88', color: '#00ff88' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' },
  
  card: {
    backgroundColor: '#111', border: '1px solid #222', borderRadius: '6px',
    padding: '14px', transition: 'border-color 0.15s',
  },
  cardLocked: { opacity: 0.5 },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: '12px',
  },
  crewName: { fontSize: '13px', fontWeight: '600', color: '#fff' },
  crewTitle: { fontSize: '10px', color: '#888', marginTop: '2px' },
  tierBadge: {
    fontSize: '9px', fontWeight: '600', letterSpacing: '1px',
    padding: '2px 6px', border: '1px solid', borderRadius: '3px',
  },
  
  statsGrid: { marginBottom: '10px' },
  statBar: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' },
  statLabel: { fontSize: '9px', color: '#666', width: '28px' },
  statTrack: {
    flex: 1, height: '4px', backgroundColor: '#222', borderRadius: '2px', overflow: 'hidden',
  },
  statFill: { height: '100%', borderRadius: '2px', transition: 'width 0.3s' },
  statValue: { fontSize: '10px', color: '#888', width: '24px', textAlign: 'right' },
  
  experience: { fontSize: '10px', color: '#555', marginBottom: '10px' },
  
  certsSection: { marginBottom: '10px' },
  certsLabel: { fontSize: '9px', color: '#666', letterSpacing: '1px', marginBottom: '4px' },
  certs: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  certBadge: {
    fontSize: '9px', color: '#888', backgroundColor: '#1a1a1a',
    padding: '2px 6px', borderRadius: '3px',
  },
  
  traitsSection: { display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '10px' },
  traitBadge: { fontSize: '9px', padding: '2px 6px', borderRadius: '3px' },
  traitPositive: { backgroundColor: '#0a2a0a', color: '#00ff88' },
  traitNegative: { backgroundColor: '#2a0a0a', color: '#ff6666' },
  
  cardFooter: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
    borderTop: '1px solid #222', paddingTop: '10px', marginTop: '10px',
  },
  salary: { fontSize: '12px', color: '#fff' },
  bonus: { fontSize: '10px', color: '#666' },
  hireBtn: {
    background: 'none', border: '1px solid #00ff88', borderRadius: '3px',
    padding: '6px 14px', fontSize: '10px', color: '#00ff88', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnLocked: { borderColor: '#333', color: '#666', cursor: 'not-allowed' },
  fireBtn: {
    background: 'none', border: '1px solid #661111', borderRadius: '3px',
    padding: '4px 10px', fontSize: '9px', color: '#ff4444', cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

export default SupportCrewPanel;
